import { isSharedBrandSession } from "./loginAccount";

export const hasValidToken = (token) => {
  if (!token) {
    return false;
  }

  if (typeof token === "string") {
    return token.trim().length > 0;
  }

  if (typeof token === "object") {
    if (typeof token.token === "string") {
      return token.token.trim().length > 0;
    }

    if (typeof token.accessToken === "string") {
      return token.accessToken.trim().length > 0;
    }

    return Object.keys(token).length > 0;
  }

  return false;
};

export const getStoredUserId = (user) => {
  return user?.userId || user?.id || "";
};

export const RENEW_SIGN_IN_KEY = "matlib-renew-sign-in";

export const readStoredToken = () => {
  const raw = localStorage.getItem("token");
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch (error) {
    return raw;
  }
};

export const isLoggedInUserInfo = (userInformation) => {
  return Boolean(
    userInformation?.isLoggedIn === true && getStoredUserId(userInformation),
  );
};

export const accessTokenValue = (token) => {
  if (typeof token === "string") {
    return token.trim();
  }
  if (typeof token?.accessToken === "string") {
    return token.accessToken.trim();
  }
  if (typeof token?.token === "string") {
    return token.token.trim();
  }
  return "";
};

function decodeJwtPayload(token) {
  const value = accessTokenValue(token);
  const parts = value.split(".");
  if (parts.length !== 3 || !parts[1]) {
    return null;
  }

  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch (error) {
    return null;
  }
}

export const isCurrentAccessToken = (token, now = Date.now()) => {
  const exp = Number(decodeJwtPayload(token)?.exp);
  return Number.isFinite(exp) && exp * 1000 > now;
};

export const isAuthenticatedUser = (user, token) => {
  return Boolean(getStoredUserId(user) && isCurrentAccessToken(token));
};

export const markSignInForRenewal = () => {
  try {
    sessionStorage.setItem(RENEW_SIGN_IN_KEY, "1");
  } catch (error) {
    // Session storage can be unavailable in private browsing.
  }
};

export const consumeSignInRenewal = () => {
  try {
    const marked = sessionStorage.getItem(RENEW_SIGN_IN_KEY) === "1";
    if (marked) {
      sessionStorage.removeItem(RENEW_SIGN_IN_KEY);
    }
    return marked;
  } catch (error) {
    return false;
  }
};

export const getAccessToken = () => accessTokenValue(readStoredToken());

export const authHeaders = () => {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const normalizeUserType = (userInformation) =>
  String(userInformation?.userType || "").trim().toLowerCase();

export const isVendorAdminUser = (userInformation) => {
  return Boolean(
    isLoggedInUserInfo(userInformation) &&
    normalizeUserType(userInformation) === "vendoradmin",
  );
};

export const isVendorUser = (userInformation) => {
  const userType = normalizeUserType(userInformation);
  return Boolean(
    isLoggedInUserInfo(userInformation) &&
    (userType === "vendor" || userType === "vendoradmin"),
  );
};

export const isDesignerViewer = (userInformation) => {
  return Boolean(
    isLoggedInUserInfo(userInformation) && !isVendorUser(userInformation),
  );
};

export const canAccessRegister = (userInformation) => {
  return (
    isVendorAdminUser(userInformation) || isSharedBrandSession(userInformation)
  );
};
